// Feature-local data hooks for the RTP Planner (precedent: src/hooks/usePlayerNotes.js).
// No Redux slice for this feature — approved deviation, see the frontend plan doc.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { TrainingAPI } from "@api/TrainingAPI";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { getRtpErrorMessage } from "@utils/rtp_utils";
import type {
	RtpBlockLibrary,
	RtpEventsResponse,
	RtpMetricProgress,
	RtpMetricStatRow,
	RtpNote,
	RtpPlanDetail,
	RtpPlanListItem,
	RtpPlanStatus,
} from "./types";

export const rtpApi = new TrainingAPI();

interface RtpResource<T> {
	data: T;
	loading: boolean;
	error: boolean;
	// Resolves when the load ends. A failed load shows its error and still resolves.
	refetch: () => Promise<void>;
}

// requestId guards a stale response (deps changed again before the first call resolved, or the
// component unmounted) from overwriting state with an out-of-order result.
function useRtpResource<T>(
	load: () => Promise<T>,
	deps: unknown[],
	defaultValue: T
): RtpResource<T> {
	const [data, setData] = useState<T>(defaultValue);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const requestId = useRef(0);

	const refetch = useCallback(() => {
		const id = ++requestId.current;
		setLoading(true);
		setError(false);
		return load()
			.then((result) => {
				if (id !== requestId.current) return;
				setData(result);
			})
			.catch((e) => {
				if (id !== requestId.current) return;
				setError(true);
				message.error(getRtpErrorMessage(e));
			})
			.finally(() => {
				if (id === requestId.current) setLoading(false);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);

	useEffect(() => {
		refetch();
	}, [refetch]);

	return { data, loading, error, refetch };
}

export function useRtpPlans(
	filters: { playerId?: number; status?: RtpPlanStatus } = {}
) {
	const { playerId, status } = filters;
	const {
		data: plans,
		loading,
		error,
		refetch,
	} = useRtpResource<RtpPlanListItem[]>(
		() => rtpApi.getRtpPlans({ playerId, status }),
		[playerId, status],
		[]
	);
	return { plans, loading, error, refetch };
}

export function useRtpPlan(planId: number) {
	const {
		data: plan,
		loading,
		error,
		refetch,
	} = useRtpResource<RtpPlanDetail | null>(
		() => rtpApi.getRtpPlan(planId),
		[planId],
		null
	);
	return { plan, loading, error, refetch };
}

export function useRtpNotes(planId: number) {
	const {
		data: notes,
		loading,
		error,
		refetch,
	} = useRtpResource<RtpNote[]>(() => rtpApi.getRtpPlanNotes(planId), [planId], []);
	return { notes, loading, error, refetch };
}

export function useRtpEvents(planId: number, page: number) {
	const { data, loading, error, refetch } = useRtpResource<RtpEventsResponse>(
		() => rtpApi.getRtpPlanEvents(planId, page),
		[planId, page],
		{ events: [], total: 0, page, pageSize: 50 }
	);
	return {
		events: data.events,
		total: data.total,
		pageSize: data.pageSize,
		loading,
		error,
		refetch,
	};
}

// The shared block library list. Call it once per plan page and pass the result down. Every
// write calls `reload`. A failed load shows the error and leaves an empty list that cannot create.
const EMPTY_BLOCK_LIBRARY: RtpBlockLibrary = { canCreate: false, templates: [] };

export type RtpBlockLibraryState = RtpBlockLibrary & { loading: boolean; reload: () => Promise<void> };

export function useRtpBlockLibrary(): RtpBlockLibraryState {
	const { data, loading, error, refetch } = useRtpResource<RtpBlockLibrary>(
		() => rtpApi.getRtpBlockTemplates(),
		[],
		EMPTY_BLOCK_LIBRARY
	);
	return { ...(error ? EMPTY_BLOCK_LIBRARY : data), loading, reload: refetch };
}

export function useCurrentUserUuid(): string {
	// Confirmed: GET /current_user/ stringifies GenericUser.uuid here; `user_id` is the integer pk — wrong for RTP identity.
	return useSelector((state: any) => state.auth.currentUser?.uuid) ?? "";
}

export interface Coach {
	user_id: string;
	uuid: string;
	first_name: string;
	last_name: string;
	email?: string;
}

export function useCoachList(): Coach[] {
	const coachList = useSelector((state: any) => state.team?.info?.coach_list ?? []);
	// Permission-merged/demo coach rows lack a real uuid (missing or the literal "dummy") and can't hold RTP roles — drop them.
	return coachList.filter((c: Coach) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(c.uuid ?? ""));
}

export function coachName(c: Coach): string {
	return `${c.first_name} ${c.last_name}`.trim();
}

// Advisory threshold hint for the criteria form — refetches when the picked metric changes.
// Errors stay silent on purpose: this is a hint, and toasting on every dropdown change of a
// metric without history would be noise. A failed/empty fetch just hides the hint panel.
export function useRtpMetricStats(
	playerId: number | null | undefined,
	metricKey: string | null | undefined
): { rows: RtpMetricStatRow[]; loading: boolean } {
	const [rows, setRows] = useState<RtpMetricStatRow[]>([]);
	const [loading, setLoading] = useState(false);
	const requestId = useRef(0);

	useEffect(() => {
		const id = ++requestId.current;
		setRows([]);
		if (!playerId || !metricKey) {
			setLoading(false);
			return;
		}
		setLoading(true);
		rtpApi
			.getRtpMetricStats(playerId, metricKey)
			.then((data: unknown) => {
				if (id !== requestId.current) return;
				// The backend sometimes appends its own `recommendation` row — dropped, since the
				// UI computes the recommendation from the max/baseline/histAvg rows itself.
				const valid = (Array.isArray(data) ? data : []).filter(
					(row): row is RtpMetricStatRow =>
						typeof row?.label === "string" && row?.value != null && row?.key !== "recommendation"
				);
				setRows(valid);
			})
			.catch(() => {
				if (id === requestId.current) setRows([]);
			})
			.finally(() => {
				if (id === requestId.current) setLoading(false);
			});
	}, [playerId, metricKey]);

	return { rows, loading };
}

// One whole-plan series per metric for the Progress tab — fetched once per plan, the
// Phase/Week pills slice client-side. allSettled so one failed metric leaves the others'
// cards intact (that metric's card shows its own error state); a single toast for the
// first failure instead of one per metric.
export function useRtpMetricProgress(
	planId: number,
	metricKeys: string[]
): { byKey: Record<string, RtpMetricProgress>; loading: boolean } {
	const [byKey, setByKey] = useState<Record<string, RtpMetricProgress>>({});
	const [loading, setLoading] = useState(true);
	const requestId = useRef(0);
	const keys = metricKeys.join(",");

	useEffect(() => {
		const id = ++requestId.current;
		const list = keys ? keys.split(",") : [];
		if (list.length === 0) {
			setByKey({});
			setLoading(false);
			return;
		}
		setLoading(true);
		Promise.allSettled(list.map((key) => rtpApi.getRtpMetricProgress(planId, key))).then((results) => {
			if (id !== requestId.current) return;
			const next: Record<string, RtpMetricProgress> = {};
			results.forEach((result, i) => {
				if (result.status === "fulfilled") next[list[i]] = result.value;
			});
			const failed = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
			if (failed) message.error(getRtpErrorMessage(failed.reason));
			setByKey(next);
			setLoading(false);
		});
	}, [planId, keys]);

	return { byKey, loading };
}

export type RtpMetricSource = "gps" | "testing" | "forceplate" | "nutrition";

// Display names per source — "wellness" kept for criteria stored before it was dropped
// from the picker.
export const RTP_SOURCE_LABELS: Record<string, string> = {
	gps: "GPS",
	testing: "Testing",
	forceplate: "Forceplate",
	nutrition: "Nutrition",
	wellness: "Wellness",
};

interface RtpMetricGroup {
	label: string;
	title: string;
	options: { value: string; label: string }[];
}

// Grouped metric options for the exit-criteria metric dropdown — same source and value
// convention as the Compare 1v1 metric picker (team.all_metric_types, value =
// unique_identifier || `${type}-${metric_id}`), limited to the sources the RTP backend
// accepts as `metricSource`. `byKey` resolves a stored metricKey back to a name + source.
export function useRtpMetricOptions(
	sources: RtpMetricSource[] = ["gps", "testing", "forceplate", "nutrition"]
): {
	groups: RtpMetricGroup[];
	byKey: Record<string, { label: string; source: RtpMetricSource }>;
} {
	const allMetricTypes = useSelector((state: any) => state.team?.all_metric_types);
	return useMemo(() => {
		const groups: RtpMetricGroup[] = [];
		const byKey: Record<string, { label: string; source: RtpMetricSource }> = {};
		for (const type of sources) {
			const label = RTP_SOURCE_LABELS[type];
			const metrics = (allMetricTypes?.[type] ?? []).filter(
				(m: any) => m.visible === true || m.visible === undefined
			);
			if (metrics.length === 0) continue;
			const options = metrics.map((m: any) => {
				const value = m.unique_identifier || `${type}-${m.metric_id}`;
				byKey[value] = { label: m.name, source: type };
				return { value, label: m.name };
			});
			// GPS keeps the backend's order (Compare 1v1 parity); the rest sort alphabetically.
			if (type !== "gps") {
				options.sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
			}
			groups.push({ label, title: label, options });
		}
		return { groups, byKey };
		// join() so an inline sources array doesn't bust the memo every render
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allMetricTypes, sources.join(",")]);
}

export async function runRtpWrite(
	write: () => Promise<unknown>,
	onSuccess?: () => void
): Promise<boolean> {
	try {
		await write();
		onSuccess?.();
		return true;
	} catch (e) {
		message.error(getRtpErrorMessage(e));
		return false;
	}
}
